/**
 * The CRM wire contract, frontend half (docs/crm-module/crm-api-contract.md, C0.05).
 *
 * Every closed set the backend declares in `backend/modules/crm/schemas.py` and
 * `backend/modules/automation/schemas.py` is mirrored here BY HAND and pinned by
 * `backend/tests/test_crm_frontend_constants_pinned.py`, so a member added on one side
 * goes red on the other instead of reaching a practitioner as a raw identifier.
 *
 * Each enum has a matching `*_KEYS` map from backend value to a LITERAL i18n key: the
 * key-coverage scanner (`i18n/__tests__/keyCoverage.test.js`) cannot resolve a key
 * built with a template string, so components look labels up through these maps and
 * `admin/crm/__tests__/enumKeys.test.js` asserts every member has fr/en/es copy.
 *
 * Merge tokens (`{client_name}` …) are SYNTAX, not copy: they are typed into email
 * bodies and survive `t()` untouched because `interpolate` only replaces the variables a
 * call passes (i18n/index.js).
 */

/** Paths under `/api` (lib/api's baseURL). Functions build the id-scoped ones. */
export const CRM_PATHS = Object.freeze({
  today: "/crm/dashboard/today",
  resendReminder: (bookingId) => `/crm/dashboard/today/${bookingId}/resend-reminder`,
  nudges: "/crm/nudges",
  nudgeDraft: (id) => `/crm/nudges/${id}/draft`,
  nudgeAction: (id) => `/crm/nudges/${id}/action`,
  nudgeDismiss: (id) => `/crm/nudges/${id}/dismiss`,
  performance: "/crm/dashboard/performance",
  performanceExport: "/crm/dashboard/performance/export",
  setup: "/crm/setup",
  setupReminders: "/crm/setup/reminders",
  setupBannerDismiss: "/crm/setup/banner/dismiss",
  promotions: "/crm/promotions",
  promotionStop: (id) => `/crm/promotions/${id}/stop`,
  // crm-module BEGIN C3.02/C3.04 — Promote quote (lane F4, append-only).
  promotionQuote: "/crm/promotions/quote",
  // crm-module END
  clients: "/crm/clients",
  clientLookup: "/crm/clients/lookup",
  clientsBulk: "/crm/clients/bulk",
  // Owner decision 2026-10-21: owner-only bulk delete, apart from the member-level bulk route.
  clientsBulkDelete: "/crm/clients/bulk-delete",
  clientsExport: "/crm/clients/export",
  tags: "/crm/tags",
  views: "/crm/views",
  view: (id) => `/crm/views/${id}`,
  importsLatest: "/crm/imports/latest",
  client: (id) => `/crm/clients/${id}`,
  clientStage: (id) => `/crm/clients/${id}/stage`,
  clientStageResume: (id) => `/crm/clients/${id}/stage/resume`,
  clientTimeline: (id) => `/crm/clients/${id}/timeline`,
  clientInteractions: (id) => `/crm/clients/${id}/interactions`,
  clientNotes: (id) => `/crm/clients/${id}/notes`,
  clientNote: (id, noteId) => `/crm/clients/${id}/notes/${noteId}`,
  clientDocuments: (id) => `/crm/clients/${id}/documents`,
  clientDocument: (id, docId) => `/crm/clients/${id}/documents/${docId}`,
  clientLinks: (id) => `/crm/clients/${id}/links`,
  clientLink: (id, linkId) => `/crm/clients/${id}/links/${linkId}`,
  clientSessions: (id) => `/crm/clients/${id}/sessions`,
  clientPurchases: (id) => `/crm/clients/${id}/purchases`,
  clientEmails: (id) => `/crm/clients/${id}/emails`,
  clientEmail: (id, sendId) => `/crm/clients/${id}/emails/${sendId}`,
  clientMergeCandidates: (id) => `/crm/clients/${id}/merge/candidates`,
  clientMergePreview: (id) => `/crm/clients/${id}/merge/preview`,
  clientMerge: (id) => `/crm/clients/${id}/merge`,
  clientUnsubscribe: (id) => `/crm/clients/${id}/unsubscribe`,
  clientErase: (id) => `/crm/clients/${id}/erase`,
  clientDataExport: (id) => `/crm/clients/${id}/data-export`,
  clientSessionNotes: (id) => `/crm/clients/${id}/session-notes`,
  clientSessionNote: (id, bookingId) => `/crm/clients/${id}/session-notes/${bookingId}`,
  clientPacks: (id) => `/crm/clients/${id}/packs`,
  clientPackAdjust: (id, balanceId) => `/crm/clients/${id}/packs/${balanceId}/adjust`,
  packs: "/crm/packs",
  // crm-module BEGIN C2.03 — Packs catalogue CRUD + a client balance's Void (lane F2).
  pack: (id) => `/crm/packs/${id}`,
  packArchive: (id) => `/crm/packs/${id}/archive`,
  packUnarchive: (id) => `/crm/packs/${id}/unarchive`,
  clientPackVoid: (id, balanceId) => `/crm/clients/${id}/packs/${balanceId}/void`,
  // crm-module END
  // crm-module BEGIN C2.04 — Capture form admin (lane F2).
  formsDefault: "/crm/forms/default",
  form: (id) => `/crm/forms/${id}`,
  formRotateKey: (id) => `/crm/forms/${id}/rotate-key`,
  // crm-module END
  imports: "/crm/imports",
  importJob: (id) => `/crm/imports/${id}`,
  importMapping: (id) => `/crm/imports/${id}/mapping`,
  importDuplicates: (id) => `/crm/imports/${id}/duplicates`,
  importConsent: (id) => `/crm/imports/${id}/consent`,
  importSummary: (id) => `/crm/imports/${id}/summary`,
  importStart: (id) => `/crm/imports/${id}/start`,
  importSkipped: (id) => `/crm/imports/${id}/skipped.csv`,
  importDismiss: (id) => `/crm/imports/${id}/dismiss`,
  attendance: (bookingId) => `/admin/bookings/${bookingId}/attendance`,
  adminBookings: "/admin/bookings",
  offeredStarts: "/admin/booking/offered-starts",
  bookingServices: "/booking/services",
  sequences: "/automation/sequences",
  // crm-module BEGIN C2.05 — Email Automation: sequences, broadcasts, birthday, pack
  // renewal and the sending domain (lane F1).
  sequence: (id) => `/automation/sequences/${id}`,
  sequenceActivate: (id) => `/automation/sequences/${id}/activate`,
  sequenceDraft: (id) => `/automation/sequences/${id}/draft`,
  sequenceDuplicate: (id) => `/automation/sequences/${id}/duplicate`,
  emailPreview: "/automation/preview",
  emailPreviewTest: "/automation/preview/test",
  broadcastsPreview: "/automation/broadcasts/preview",
  broadcasts: "/automation/broadcasts",
  broadcast: (id) => `/automation/broadcasts/${id}`,
  birthday: "/automation/birthday",
  packRenewal: "/automation/pack-renewal",
  sendingDomain: "/automation/sending-domain",
  sendingDomainVerify: "/automation/sending-domain/verify",
  // crm-module END
  media: "/admin/media",
  adminProducts: "/admin/products",
  learnCourses: "/learn/courses",
  // Pathways phase 3 — the sequence editor's pathway picker (pathways-api-contract §5.1).
  pathways: "/pathways",
  // crm-module BEGIN C3.02 — Team (lane F5): invite by email, list, resend, remove;
  // the accept endpoint a freshly-signed-in helper's JoinPage calls.
  me: "/crm/me",
  team: "/crm/team",
  teamInvites: "/crm/team/invites",
  teamInviteResend: (id) => `/crm/team/invites/${id}/resend`,
  teamMember: (id) => `/crm/team/${id}`,
  teamAccept: "/crm/team/accept",
  // crm-module END
  // crm-module BEGIN CR-F3-1 — CRM Settings (owner-only GET/PUT of `settings.crm`).
  settings: "/crm/settings",
  // crm-module END
});

/** Clients list page size (spec: "50 per page"; contract C0.01 default). */
export const PAGE_SIZE = 50;
/** Nudges shown before "Show all" (spec: "max 5"). */
export const NUDGE_LIMIT = 5;
/** A pack is "ending" at this many sessions left (spec: "amber at 2 left"). */
export const PACK_WARN_AT = 2;

// crm-module BEGIN CR-F3-1
/**
 * The practitioner-editable numbers on Admin → CRM → Settings, with the bounds the
 * server's `CrmSettingsUpdate` refuses outside of (422). Pinned against
 * `backend/modules/crm/settings.py` by tests/test_crm_settings_bounds_pinned.py. The
 * order is the screen's order.
 */
export const CRM_SETTINGS_BOUNDS = Object.freeze({
  pack_ending_threshold: { min: 0, max: 100 },
  inactivity_days: { min: 7, max: 3650 },
  gone_quiet_weeks: { min: 1, max: 104 },
  review_threshold: { min: 1, max: 1000 },
  daily_send_cap: { min: 0, max: 100000 },
});
// crm-module END
/** Import ceiling in v1 (spec: "Max 10,000 rows"). */
export const IMPORT_MAX_ROWS = 10000;
/** Live search starts at this many characters (spec: "Live after 3 characters"). */
export const SEARCH_MIN_CHARS = 3;
/** Timeline and notes page size. */
export const FEED_PAGE_SIZE = 20;
/** The bulk ceiling the server enforces (422 BULK_TOO_LARGE). */
export const BULK_MAX = 10000;

export const STAGES = Object.freeze(["lead", "active", "past"]);
export const STAGE_KEYS = Object.freeze({
  lead: "enum.stage.lead",
  active: "enum.stage.active",
  past: "enum.stage.past",
});
/** The short badge form used in dense rows (wireframe: "Active", "Lead", "Past"). */
export const STAGE_SHORT_KEYS = Object.freeze({
  lead: "enum.stageShort.lead",
  active: "enum.stageShort.active",
  past: "enum.stageShort.past",
});
export const STAGE_MODES = Object.freeze(["auto", "manual"]);

export const SOURCES = Object.freeze(["import", "booking", "purchase", "form", "pathway", "marketplace", "manual", "signup"]);
export const SOURCE_KEYS = Object.freeze({
  import: "enum.source.import",
  booking: "enum.source.booking",
  purchase: "enum.source.purchase",
  form: "enum.source.form",
  pathway: "enum.source.pathway",
  marketplace: "enum.source.marketplace",
  manual: "enum.source.manual",
  signup: "enum.source.signup",
});

export const CONSENT = Object.freeze(["consented", "not_consented", "unsubscribed"]);
export const CONSENT_KEYS = Object.freeze({
  consented: "enum.consent.consented",
  not_consented: "enum.consent.not_consented",
  unsubscribed: "enum.consent.unsubscribed",
});
export const CONSENT_SOURCES = Object.freeze(["form_tick", "import_declaration", "signup_tick",
  "booking_tick", "checkout_tick", "portal"]);
export const CONSENT_SOURCE_KEYS = Object.freeze({
  form_tick: "enum.consentSource.form_tick",
  import_declaration: "enum.consentSource.import_declaration",
  signup_tick: "enum.consentSource.signup_tick",
  booking_tick: "enum.consentSource.booking_tick",
  checkout_tick: "enum.consentSource.checkout_tick",
  portal: "enum.consentSource.portal",
});

export const PACK_STATES = Object.freeze(["normal", "ending", "finished"]);
// crm-module BEGIN C2.03 — the pack CATALOGUE's own status (draft/sold-out never modelled;
// a pack is simply active or archived), distinct from PACK_STATES (a client's balance).
export const PACK_CATALOG_STATUSES = Object.freeze(["active", "archived"]);
export const PACK_CATALOG_STATUS_KEYS = Object.freeze({
  active: "enum.packCatalogStatus.active",
  archived: "enum.packCatalogStatus.archived",
});
// crm-module END
/** Filter values of the Pack status dropdown (`pack_status`). */
export const PACK_FILTERS = Object.freeze(["active", "ending", "finished"]);
export const PACK_FILTER_KEYS = Object.freeze({
  active: "enum.packFilter.active",
  ending: "enum.packFilter.ending",
  finished: "enum.packFilter.finished",
});
export const INACTIVE_DAYS = Object.freeze([30, 60, 90]);
export const INACTIVE_KEYS = Object.freeze({
  30: "enum.inactive.30",
  60: "enum.inactive.60",
  90: "enum.inactive.90",
});

export const PERIODS = Object.freeze(["7d", "30d", "90d", "ytd", "custom"]);
export const PERIOD_KEYS = Object.freeze({
  "7d": "enum.period.7d",
  "30d": "enum.period.30d",
  "90d": "enum.period.90d",
  ytd: "enum.period.ytd",
  custom: "enum.period.custom",
});
export const DEFAULT_PERIOD = "30d";

export const DELIVERY_MODES = Object.freeze(["video", "in_person"]);
export const DELIVERY_KEYS = Object.freeze({
  video: "enum.delivery.video",
  in_person: "enum.delivery.in_person",
});

export const ATTENDANCE = Object.freeze(["completed", "no_show"]);

export const SESSION_STATUSES = Object.freeze(["upcoming", "completed", "no_show", "cancelled"]);
export const SESSION_STATUS_KEYS = Object.freeze({
  upcoming: "enum.sessionStatus.upcoming",
  completed: "enum.sessionStatus.completed",
  no_show: "enum.sessionStatus.no_show",
  cancelled: "enum.sessionStatus.cancelled",
});

export const INTERACTION_TYPES = Object.freeze(["call", "meeting", "note"]);
export const INTERACTION_KEYS = Object.freeze({
  call: "enum.interaction.call",
  meeting: "enum.interaction.meeting",
  note: "enum.interaction.note",
});

// crm-module BEGIN C3.02/C3.03 — a logged meeting's Google Calendar sync outcome
// (`crm_interactions.calendar.status`, backend/modules/crm/calendar.py; lane F5). `null`
// on the row (a call/note, or a meeting logged without both `start_at`/`end_at`) renders
// no indicator at all — there was nothing to sync.
export const CALENDAR_SYNC_STATUSES = Object.freeze(["synced", "no_connection", "failed"]);
export const CALENDAR_SYNC_KEYS = Object.freeze({
  synced: "log.calendarSynced",
  no_connection: "log.calendarNoConnection",
  failed: "log.calendarFailed",
});
/** StatusBadge tone per status (theme/states.js): synced reads as done, a missing
 * connection is a neutral fact (not the practitioner's error), a transient failure is
 * the admin's "needs attention" danger tone. */
export const CALENDAR_SYNC_TONE = Object.freeze({
  synced: "positive",
  no_connection: "neutral",
  failed: "danger",
});
// crm-module END

export const TIMELINE_CATEGORIES = Object.freeze(["email", "purchase", "session", "note", "other"]);
/** The feed's filter chips, in the spec's order ("All, Emails, Purchases, Sessions, Notes"). */
export const TIMELINE_FILTERS = Object.freeze(["all", "email", "purchase", "session", "note"]);
export const TIMELINE_FILTER_KEYS = Object.freeze({
  all: "timeline.filter.all",
  email: "timeline.filter.email",
  purchase: "timeline.filter.purchase",
  session: "timeline.filter.session",
  note: "timeline.filter.note",
});
/**
 * Timeline row kinds, keyed on the WIRE value as-is (contract v1.4): an ingested event's
 * row carries the bus event's own dotted name (`booking.created`), a fact the CRM records
 * itself an underscore name (`stage_changed`, `interaction_call`). Mirrors
 * `backend/modules/crm/schemas.py` TIMELINE_KINDS. A kind outside this map — one a later
 * backend adds before this file does — renders with its CATEGORY's generic title, never
 * as the raw identifier.
 */
export const TIMELINE_KIND_KEYS = Object.freeze({
  "blog.commented": "timeline.kind.blog_commented",
  "booking.cancelled": "timeline.kind.booking_cancelled",
  "booking.completed": "timeline.kind.booking_completed",
  "booking.confirmed": "timeline.kind.booking_confirmed",
  "booking.confirmed_via_reminder": "timeline.kind.booking_confirmed_via_reminder",
  "booking.created": "timeline.kind.booking_created",
  "booking.no_show": "timeline.kind.booking_no_show",
  "booking.rescheduled": "timeline.kind.booking_rescheduled",
  consent_changed: "timeline.kind.consent_changed",
  "course.enrolled": "timeline.kind.course_enrolled",
  created: "timeline.kind.created",
  imported: "timeline.kind.imported",
  interaction_call: "timeline.kind.interaction_call",
  interaction_meeting: "timeline.kind.interaction_meeting",
  interaction_note: "timeline.kind.interaction_note",
  legacy: "timeline.kind.legacy",
  merged: "timeline.kind.merged",
  "message.received": "timeline.kind.message_received",
  "message.replied": "timeline.kind.message_replied",
  note_added: "timeline.kind.note_added",
  "pathway.certificate_issued": "timeline.kind.pathway_certificate_issued",
  "pathway.completed": "timeline.kind.pathway_completed",
  "pathway.encouragement_sent": "timeline.kind.pathway_encouragement_sent",
  "pathway.feedback_sent": "timeline.kind.pathway_feedback_sent",
  "pathway.enrolled": "timeline.kind.pathway_enrolled",
  "pathway.level_completed": "timeline.kind.pathway_level_completed",
  "pathway.step_completed": "timeline.kind.pathway_step_completed",
  "payment.refunded": "timeline.kind.payment_refunded",
  "payment.succeeded": "timeline.kind.payment_succeeded",
  session_note_saved: "timeline.kind.session_note_saved",
  stage_changed: "timeline.kind.stage_changed",
  // C2.03 (packs)
  pack_assigned: "timeline.kind.pack_assigned",
  pack_adjusted: "timeline.kind.pack_adjusted",
  pack_voided: "timeline.kind.pack_voided",
  // C2.04 (capture form)
  form_submitted: "timeline.kind.form_submitted",
  // v1.25 (sign-in on the practice site)
  signed_up: "timeline.kind.signed_up",
  // C2.07 (automation send path)
  email_sent: "timeline.kind.email_sent",
});
export const TIMELINE_CATEGORY_KEYS = Object.freeze({
  email: "timeline.category.email",
  purchase: "timeline.category.purchase",
  session: "timeline.category.session",
  note: "timeline.category.note",
  other: "timeline.category.other",
});

export const PURCHASE_KINDS = Object.freeze(["course", "product", "booking", "program", "pack", "pathway", "giftcard", "invoice"]);
export const PURCHASE_KIND_KEYS = Object.freeze({
  course: "enum.purchaseKind.course",
  product: "enum.purchaseKind.product",
  booking: "enum.purchaseKind.booking",
  program: "enum.purchaseKind.program",
  pack: "enum.purchaseKind.pack",
  pathway: "enum.purchaseKind.pathway",
  giftcard: "enum.purchaseKind.giftcard",
  invoice: "enum.purchaseKind.invoice",
});
export const PURCHASE_STATUSES = Object.freeze(["paid", "free", "pending", "refunded", "partially_refunded", "cancelled"]);
export const PURCHASE_STATUS_KEYS = Object.freeze({
  paid: "enum.purchaseStatus.paid",
  free: "enum.purchaseStatus.free",
  pending: "enum.purchaseStatus.pending",
  refunded: "enum.purchaseStatus.refunded",
  partially_refunded: "enum.purchaseStatus.partially_refunded",
  cancelled: "enum.purchaseStatus.cancelled",
});
export const CHANNELS = Object.freeze(["online", "offline"]);
export const CHANNEL_KEYS = Object.freeze({
  online: "enum.channel.online",
  offline: "enum.channel.offline",
});

/** Top offerings "type" column (wireframe: Course, Pack, Session; CR-F-2). */
export const OFFERING_TYPES = Object.freeze(["course", "product", "pack", "session"]);
export const OFFERING_TYPE_KEYS = Object.freeze({
  course: "enum.offering.course",
  product: "enum.offering.product",
  pack: "enum.offering.pack",
  session: "enum.offering.session",
});

export const EMAIL_STATUSES = Object.freeze([
  "queued", "deferred_cap", "sending", "sent", "delivered", "opened",
  "bounced", "complained", "failed", "skipped_consent", "skipped_suppressed",
]);
export const EMAIL_STATUS_KEYS = Object.freeze({
  queued: "enum.emailStatus.queued",
  deferred_cap: "enum.emailStatus.deferred_cap",
  sending: "enum.emailStatus.sending",
  sent: "enum.emailStatus.sent",
  delivered: "enum.emailStatus.delivered",
  opened: "enum.emailStatus.opened",
  bounced: "enum.emailStatus.bounced",
  complained: "enum.emailStatus.complained",
  failed: "enum.emailStatus.failed",
  skipped_consent: "enum.emailStatus.skipped_consent",
  skipped_suppressed: "enum.emailStatus.skipped_suppressed",
});
export const EMAIL_ORIGINS = Object.freeze(["sequence", "broadcast", "birthday", "manual", "nudge", "pack_renewal", "reminder_resend", "test"]);
export const EMAIL_ORIGIN_KEYS = Object.freeze({
  sequence: "enum.emailOrigin.sequence",
  broadcast: "enum.emailOrigin.broadcast",
  birthday: "enum.emailOrigin.birthday",
  manual: "enum.emailOrigin.manual",
  nudge: "enum.emailOrigin.nudge",
  pack_renewal: "enum.emailOrigin.pack_renewal",
  reminder_resend: "enum.emailOrigin.reminder_resend",
  test: "enum.emailOrigin.test",
});

// crm-module BEGIN C2.02/C2.05 — Email Automation closed sets (lane F1): sequence status
// and stream, the five system sequences, trigger types, step-offset units/anchors and the
// sending-domain verification states. Mirrors backend/modules/automation/schemas.py.
export const SEQUENCE_STATUSES = Object.freeze(["draft", "active"]);
export const SEQUENCE_STATUS_KEYS = Object.freeze({
  draft: "enum.sequenceStatus.draft",
  active: "enum.sequenceStatus.active",
});
export const STREAMS = Object.freeze(["marketing", "transactional"]);

/** The five pre-built sequences (spec Screen 6), in the wireframe's order; `null` = custom. */
export const SYSTEM_KEYS = Object.freeze(["welcome", "session_reminders", "follow_up", "re_engagement", "review_request"]);
export const SYSTEM_KEY_NAME_KEYS = Object.freeze({
  welcome: "seq.system.welcome",
  session_reminders: "seq.system.sessionReminders",
  follow_up: "seq.system.followUp",
  re_engagement: "seq.system.reEngagement",
  review_request: "seq.system.reviewRequest",
});

export const TRIGGER_TYPES = Object.freeze([
  "new_client", "booking_exists", "booking_completed", "purchase_completed",
  "no_activity", "pack_balance_hits", "tag_added", "nth_session_completed", "review_moment",
  // Pathways phase 3 (pathways-api-contract §10.8): params `{pathway_id: str | null, unit}`
  // and `{pathway_id: str | null}`, null = any pathway.
  "pathway_progress", "pathway_completed",
]);
export const TRIGGER_KEYS = Object.freeze({
  new_client: "seq.trigger.newClient",
  booking_exists: "seq.trigger.bookingExists",
  booking_completed: "seq.trigger.bookingCompleted",
  purchase_completed: "seq.trigger.purchaseCompleted",
  no_activity: "seq.trigger.noActivity",
  pack_balance_hits: "seq.trigger.packBalanceHits",
  tag_added: "seq.trigger.tagAdded",
  nth_session_completed: "seq.trigger.nthSessionCompleted",
  review_moment: "seq.trigger.reviewMoment",
  pathway_progress: "seq.trigger.pathwayProgress",
  pathway_completed: "seq.trigger.pathwayCompleted",
});
/**
 * The pathway triggers' parameters (pathways-api-contract §10.8): which pathway (null =
 * any), and for progress whether a STEP or a LEVEL completion fires it. Their editor is
 * the pathway picker in TriggerSelect, not the single number/tag field below.
 */
export const PATHWAY_TRIGGERS = Object.freeze(["pathway_progress", "pathway_completed"]);
export const PATHWAY_PROGRESS_UNITS = Object.freeze(["step", "level"]);
export const PATHWAY_PROGRESS_UNIT_KEYS = Object.freeze({
  step: "seq.trigger.pathwayUnit.step",
  level: "seq.trigger.pathwayUnit.level",
});
/** The params a pathway trigger starts with when it is chosen: any pathway, per step. */
export const PATHWAY_TRIGGER_DEFAULT_PARAMS = Object.freeze({
  pathway_progress: Object.freeze({ pathway_id: null, unit: "step" }),
  pathway_completed: Object.freeze({ pathway_id: null }),
});
/** Triggers that carry no extra parameter (the rest render a number/tag field beside the dropdown). */
export const TRIGGER_NO_PARAM = Object.freeze(["new_client", "booking_exists", "booking_completed", "review_moment"]);

export const OFFSET_UNITS = Object.freeze(["minutes", "hours", "days"]);
/** Plural form — the unit as it reads in the editable OffsetField dropdown ("hours before session ▾"). */
export const OFFSET_UNIT_KEYS = Object.freeze({
  minutes: "seq.offsetUnit.minutes",
  hours: "seq.offsetUnit.hours",
  days: "seq.offsetUnit.days",
});
/** Singular form — the collapsed StepSummary line pluralises by the actual value ("1 hour before session"). */
export const OFFSET_UNIT_SINGULAR_KEYS = Object.freeze({
  minutes: "seq.offsetUnitOne.minutes",
  hours: "seq.offsetUnitOne.hours",
  days: "seq.offsetUnitOne.days",
});
export const OFFSET_ANCHORS = Object.freeze(["after_trigger", "before_session"]);
export const OFFSET_ANCHOR_KEYS = Object.freeze({
  after_trigger: "seq.offsetAnchor.afterTrigger",
  before_session: "seq.offsetAnchor.beforeSession",
});
/** Only `session_reminders` (and any custom sequence triggered by `booking_exists`) may anchor a step "before session". */
export const BEFORE_SESSION_TRIGGERS = Object.freeze(["booking_exists"]);

// crm-module BEGIN C3.02 — Team member status (`tenant_members.status`, lane F5).
export const TEAM_STATUSES = Object.freeze(["invited", "active", "removed"]);
export const TEAM_STATUS_KEYS = Object.freeze({
  invited: "enum.teamStatus.invited",
  active: "enum.teamStatus.active",
  removed: "enum.teamStatus.removed",
});
// crm-module END

export const DOMAIN_STATUSES = Object.freeze(["not_started", "pending", "verified", "failed"]);
export const DOMAIN_STATUS_KEYS = Object.freeze({
  not_started: "enum.domainStatus.not_started",
  pending: "enum.domainStatus.pending",
  verified: "enum.domainStatus.verified",
  failed: "enum.domainStatus.failed",
});
// crm-module END

export const BULK_ACTIONS = Object.freeze(["add_tag", "remove_tag", "set_stage", "add_to_sequence"]);

export const SORT_FIELDS = Object.freeze([
  "name", "email", "stage", "tags", "pack_balance", "last_activity_at", "lifetime_spend_cents", "created_at",
]);

/**
 * The targets a file column can be mapped onto (CR-T-40). `skip` is the column
 * dropdown's "leave this column out" choice; a skipped column is sent ABSENT from
 * `mapping`.
 */
export const IMPORT_FIELDS = Object.freeze(["email", "name", "first_name", "last_name", "phone", "birthday", "tags", "skip"]);
export const IMPORT_SKIP = "skip";
export const IMPORT_FIELD_KEYS = Object.freeze({
  email: "import.field.email",
  name: "import.field.name",
  first_name: "import.field.first_name",
  last_name: "import.field.last_name",
  phone: "import.field.phone",
  birthday: "import.field.birthday",
  tags: "import.field.tags",
  skip: "import.field.skip",
});
export const IMPORT_STATUSES = Object.freeze(["draft", "queued", "running", "completed", "failed"]);
export const IMPORT_ERRORS = Object.freeze(["EMAIL_INVALID", "EMAIL_MISSING", "SUPPRESSED", "DUPLICATE_IN_FILE", "TOO_MANY_TAGS", "TAG_TOO_LONG"]);
export const IMPORT_ACCEPT = ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const NUDGE_TYPES = Object.freeze([
  "pack_ending", "gone_quiet", "new_client_checkin", "review_moment", "unconfirmed_tomorrow",
  // Pathways phase 3 (pathways-api-contract §10.8): one open nudge per stuck pathway.
  "pathway_stuck",
]);

/** Merge fields, in the spec's order. Syntax constants, never translated. */
export const MERGE_FIELDS = Object.freeze([
  "{client_name}", "{practice_name}", "{session_type}", "{session_time}", "{offering_name}", "{pack_remaining}",
]);

/** Error codes the CRM screens branch on (contract C0.02 and the route table). */
export const CRM_ERRORS = Object.freeze({
  clientExists: "CLIENT_EXISTS",
  clientNotFound: "CLIENT_NOT_FOUND",
  emailInvalid: "EMAIL_INVALID",
  clientNotConsented: "CLIENT_NOT_CONSENTED",
  dailySendCap: "DAILY_SEND_CAP_REACHED",
  viewNameTaken: "VIEW_NAME_TAKEN",
  bulkTooLarge: "BULK_TOO_LARGE",
  confirmNameMismatch: "CONFIRM_NAME_MISMATCH",
  deleteCountMismatch: "DELETE_COUNT_MISMATCH",
  importTooManyRows: "IMPORT_TOO_MANY_ROWS",
  importEmpty: "IMPORT_EMPTY",
  importEmailUnmapped: "IMPORT_EMAIL_UNMAPPED",
  attendanceTooEarly: "ATTENDANCE_TOO_EARLY",
  attendanceNotApplicable: "ATTENDANCE_NOT_APPLICABLE",
  attendanceLocked: "ATTENDANCE_LOCKED",
  reminderNotApplicable: "REMINDER_NOT_APPLICABLE",
  timeUnavailable: "TIME_UNAVAILABLE",
  serviceNotBookable: "SERVICE_NOT_BOOKABLE_BY_ADMIN",
  ownerOnly: "CRM_OWNER_ONLY",
  // crm-module BEGIN C2.05 — Email Automation refusals (lane F1).
  systemTriggerLocked: "SYSTEM_TRIGGER_LOCKED",
  systemSequence: "SYSTEM_SEQUENCE",
  mergeFieldUnknown: "MERGE_FIELD_UNKNOWN",
  offsetAnchorInvalid: "OFFSET_ANCHOR_INVALID",
  sequenceEmpty: "SEQUENCE_EMPTY",
  audienceChanged: "AUDIENCE_CHANGED",
  domainInvalid: "DOMAIN_INVALID",
  domainInUse: "DOMAIN_IN_USE",
  resendUnavailable: "RESEND_UNAVAILABLE",
  // crm-module END
  // crm-module BEGIN C2.03/C2.04 — pack catalogue + capture form refusals (lane F2).
  servicePerMinute: "SERVICE_PER_MINUTE",
  packBalanceOutOfRange: "PACK_BALANCE_OUT_OF_RANGE",
  originNotAllowed: "ORIGIN_NOT_ALLOWED",
  // crm-module END
  // crm-module BEGIN C3.02 — Team refusals (lane F5).
  memberExists: "MEMBER_EXISTS",
  cannotInviteOwner: "CANNOT_INVITE_OWNER",
  inviteEmailMismatch: "INVITE_EMAIL_MISMATCH",
  inviteExpired: "INVITE_EXPIRED",
  // crm-module END
});

/** Client-detail tabs, in the spec's order; `?tab=` carries the open one. */
export const DETAIL_TABS = Object.freeze(["timeline", "sessions", "session-notes", "purchases", "emails", "notes", "pathways"]);
export const DETAIL_TAB_KEYS = Object.freeze({
  timeline: "detail.tab.timeline",
  sessions: "detail.tab.sessions",
  "session-notes": "detail.tab.sessionNotes",
  purchases: "detail.tab.purchases",
  emails: "detail.tab.emails",
  notes: "detail.tab.notes",
  pathways: "detail.tab.pathways",
});
